import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Shield, User } from "lucide-react";

const empty = { name: "", email: "", password: "", role: "staff", phone: "", hourly_rate: 0 };

export default function Team() {
  const { user: me, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/team").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) return toast.error("Name, email and password are required");
    try {
      await api.post("/auth/register", form);
      toast.success("Team member added");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => { await api.put(`/team/${u.id}`, { active: !u.active }); load(); };
  const changeRole = async (u, role) => { await api.put(`/team/${u.id}`, { role }); toast.success("Role updated"); load(); };
  const saveRate = async (u, val) => { await api.put(`/team/${u.id}`, { hourly_rate: Number(val) || 0 }); toast.success("Rate updated"); load(); };
  const remove = async (id) => { try { await api.delete(`/team/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Manage crew members and their access.</p>
        {isAdmin && (
          <Button data-testid="add-member-btn" onClick={() => { setForm(empty); setOpen(true); }} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">
            <Plus className="h-4 w-4 mr-1.5" /> Add Member
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((u) => (
          <Card key={u.id} data-testid="team-card" className="p-5 border-border rounded-lg bg-card">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center text-primary font-display font-bold uppercase">
                {(u.name || "U").slice(0, 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-bold tracking-tight truncate">{u.name}</h3>
                  {u.role === "admin" ? <Shield className="h-3.5 w-3.5 text-secondary" /> : <User className="h-3.5 w-3.5 text-muted-foreground" />}
                </div>
                <div className="text-sm text-muted-foreground truncate">{u.email}</div>
                {u.phone && <div className="text-xs text-muted-foreground mt-0.5">{u.phone}</div>}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
              <span className="text-xs text-muted-foreground">Hourly rate</span>
              {isAdmin ? (
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    data-testid="member-rate-input"
                    defaultValue={u.hourly_rate || 0}
                    onBlur={(e) => saveRate(u, e.target.value)}
                    className="h-8 w-20"
                  />
                  <span className="text-xs text-muted-foreground">/hr</span>
                </div>
              ) : (
                <span className="text-sm font-semibold">{money(u.hourly_rate || 0)}/hr</span>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between">
              {isAdmin && u.id !== me.id ? (
                <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                  <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="crew_manager">Crew Manager</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline" className="capitalize">{u.role}{u.id === me.id ? " · you" : ""}</Badge>
              )}
              {isAdmin && u.id !== me.id && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} />
                    <span className="text-xs text-muted-foreground">{u.active !== false ? "Active" : "Off"}</span>
                  </div>
                  <button data-testid="delete-member-btn" onClick={() => remove(u.id)} className="p-1.5 rounded hover:bg-destructive/10 transition-colors duration-200"><Trash2 className="h-4 w-4 text-destructive" /></button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Name *</Label><Input data-testid="member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email *</Label><Input data-testid="member-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Temporary Password *</Label><Input data-testid="member-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="crew_manager">Crew Manager</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Hourly Rate ($/hr)</Label><Input data-testid="member-rate-dialog-input" type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="e.g. 22" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-member-btn" onClick={create} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
